import { CSSProperties, ReactNode, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { GrButton, GrStack, GrTypography, palette } from "@jnpmedi/grebob";
import { FileDetailViewer } from "@newComponents/common/Viewer/FileDetailViewer";
import { FileDetailViewerNavigation } from "@newComponents/common/Viewer/FileDetailViewerNavigation";
import { FileDetailViewerZoomButton } from "@newComponents/common/Viewer/FileDetailViewerZoomButton";
import { TextViewer } from "@newComponents/common/Viewer/TextViewer";
import { InformationGuide } from "@newComponents/layout/InformationGuide";
import { MvnMessage } from "@newLibs/utils";
import { useBoolean } from "@newHooks/common/useBoolean";
import { useDocumentContext } from "@newHooks/context/document/document.context";
import { enqueueSnackbar } from "@newHooks/context/snackbar/notification.context";
import { useRetryConvertFolderFilePdfMutation } from "@newHooks/mutation/folderFile/retryConvertFolderFilePdf.mutation";
import { useFolderFilePdfConvertQueueQuery } from "@newHooks/query/folderFile/folderFilePdfConvertQueue";
import { useParams } from "@newHooks/route";
import { ellipsis } from "@assets/styles/layout";
import baseMedia from "@assets/theme/baseDefine/baseMedia";
import { baseHeight } from "@assets/theme/baseSize/baseHeight";
import { baseWidth } from "@assets/theme/baseSize/baseWidth";
import { DocumentSignerSignatureExt } from "./types";
import { DOCS_DOCUMENT_LANG } from "@maven-msg/lang/docs";
import { DocumentContentType } from "@maven-rest/docs/Document.dto";
import { DocumentSignerAttrType } from "@maven-rest/docs/DocumentSign.dto";
import { DocumentAccessRequesterType } from "@maven-rest/docs/DocumentSigner.dto";
import { PdfConvertQueueStatus } from "@maven-rest/docs/pdf/ConvertQueue.dto";

interface FileDetailContainerProps {
  header?: { left?: ReactNode; title?: string; right?: ReactNode };
  leftSidebar?: { children: ReactNode };
  accessToken?: string;
  documentAccessRequesterType?: DocumentAccessRequesterType; // NOTE: (비로그인) 서명자, 참조자 전용 DocsPdfPage API 에서 요청의 주체를 구분짓기 위해 추가한 prop
  signatures?: DocumentSignerSignatureExt[];
  latestDocumentId?: bigint; // NOTE: 참조자의 경우, 전자서명 LifeCycle동안 동적으로 바뀌는 documentId를 전달한하는 소요가 생겨 해당 prop을 사용 (참조자 외에는 사용하지 않음)
  onMarkerClicked?: (signerUuid: string, attrUuid: string, attr: DocumentSignerAttrType) => void;
  onFolderFileByIdApiEnable?: () => void;
}

export function FileDetailContainer({
  header,
  leftSidebar,
  accessToken,
  documentAccessRequesterType = DocumentAccessRequesterType.Signer,
  signatures,
  latestDocumentId,
  onMarkerClicked,
  onFolderFileByIdApiEnable,
}: FileDetailContainerProps) {
  const { token } = useParams<{ token: string }>();
  const { documentFile, mode } = useDocumentContext();
  const { document } = documentFile;

  const isSidebarHidden = useBoolean(!leftSidebar?.children || mode === "VIEWER");

  const isFolderFilePdfConvertQueuePolingEnableRef = useRef<boolean>(true);

  const isDocumentPdfIdFalsy = "docsPdfId" in document && String(document.docsPdfId) === "null";

  useEffect(() => {
    if (mode === "VIEWER") {
      isSidebarHidden.onTrue();
      return;
    }

    if (leftSidebar?.children) {
      isSidebarHidden.onFalse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, leftSidebar?.children]);

  const { data: folderFilePdfConvertQueueData } = useFolderFilePdfConvertQueueQuery(
    { fileId: documentFile.resource.id },
    {
      onSuccess: (data) => {
        const convertStatus = data.convertQueue.status;
        if (convertStatus === PdfConvertQueueStatus.Done) {
          convertQPollingStop();
          return;
        }

        if (convertStatus === PdfConvertQueueStatus.Failed) {
          convertQPollingStop();
          return;
        }
      },
      enabled: isDocumentPdfIdFalsy && isFolderFilePdfConvertQueuePolingEnableRef.current,
      refetchInterval: 3000,
    }
  );

  const { mutate: retryConvertFolderFilePdf, isLoading: isRetryConvertFolderFilePdf } = useRetryConvertFolderFilePdfMutation({
    onSuccess: (data) => {
      if (data.result) {
        enqueueSnackbar({ message: MvnMessage(DOCS_DOCUMENT_LANG.PDF_RETRY_CONVERT_REQUEST_SUCCESS_TOAST) });
        onFolderFileByIdApiEnable?.();
        convertQPollingStart();
      }
    },
    onError: (error) => {
      enqueueSnackbar({
        message: MvnMessage(DOCS_DOCUMENT_LANG.PDF_RETRY_CONVERT_REQUEST_FAIL_TOAST, { message: error.responseMessage }),
        severity: "warning",
        autoHideDuration: 8000,
      });
    },
  });

  const handleClickFolderFileRetry = async () => {
    if (!document.convertRequestUuid) {
      // NOTI?
      return;
    }

    retryConvertFolderFilePdf({ fileId: documentFile.resource.id });
  };

  const convertQPollingStart = () => {
    isFolderFilePdfConvertQueuePolingEnableRef.current = true;
  };

  const convertQPollingStop = () => {
    isFolderFilePdfConvertQueuePolingEnableRef.current = false;
  };

  const isPdfDocumentType = document.contentType === DocumentContentType.DocsPdf;
  const isTextDocumentType = document.contentType === DocumentContentType.Text;
  const isValidPdfFile = isPdfDocumentType && !!document.metainfo;

  const renderLoadingViewByConvertQueueStatus = () => {
    const pdfConvertStatus = folderFilePdfConvertQueueData?.convertQueue.status;

    if (isValidPdfFile) {
      return (
        <StyledDocumentView key="maven-docs-file-detail-viewer">
          <FileDetailViewer
            fileId={documentFile.resource.id}
            onMarkerClicked={onMarkerClicked}
            signatures={signatures}
            docsPdf={{
              id: document.docsPdfId,
              metainfo: document.metainfo,
            }}
            token={token}
            accessToken={accessToken}
            documentAccessRequesterType={documentAccessRequesterType}
            latestDocumentId={latestDocumentId}
          />
        </StyledDocumentView>
      );
    }

    if (pdfConvertStatus === PdfConvertQueueStatus.Failed) {
      return (
        <InformationGuide
          key="maven-docs-pdf-convert-failed"
          confirmButton={
            <GrButton isLoading={isRetryConvertFolderFilePdf} onClick={handleClickFolderFileRetry}>
              {MvnMessage(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_PDF_RETRY_CONVERT_LABEL))}
            </GrButton>
          }
          style={DEFAULT_INFORM_VIEW_STYLE}
          emptyType="noFile"
          title={MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_PDF_CONVERTING_FAIL_TITLE)}
          description={MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_PDF_CONVERTING_FAIL_DESC)}
        />
      );
    }

    return (
      <InformationGuide
        key="maven-docs-pdf-convert-processing"
        emptyType="noFile"
        style={DEFAULT_INFORM_VIEW_STYLE}
        title={MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_PDF_CONVERTING_TITLE)}
        description=""
      />
    );
  };

  return (
    <FileDetailContainerWrapper>
      <FileDetailContainerHeaderWrapper>
        {!!header?.left && header.left}

        <TitleWrapper>
          <GrTypography
            type="Content.Title03"
            style={{ ...ellipsis, maxWidth: "400px" }}
            title={typeof header?.title === "string" ? header.title : documentFile.resource.name || ""}
          >
            {header?.title || documentFile.resource.name || ""}
          </GrTypography>
        </TitleWrapper>

        {!!header?.right && header.right}
      </FileDetailContainerHeaderWrapper>

      <FileDetailContainerMainWrapper>
        {!isSidebarHidden.value && <>{leftSidebar?.children}</>}

        <FileDetailContainerContentWrapper>
          {isPdfDocumentType && renderLoadingViewByConvertQueueStatus()}

          {isTextDocumentType && <TextViewer key="maven-docs-text-detail-viewer" content={document.content} />}

          {(isTextDocumentType || isValidPdfFile) && (
            <NavigationWrapper
              width={isSidebarHidden.value ? "100%" : `calc(100% - ${baseWidth.appSidebarICFDrawer})`}
              padding="0 24px 24px"
              justifyContent="flex-end"
              alignItems="center"
            >
              {isValidPdfFile && (
                <GrStack style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", pointerEvents: "auto" }}>
                  <FileDetailViewerNavigation totalPages={document.metainfo.pages.length} />
                </GrStack>
              )}

              <FileDetailViewerZoomButton />
            </NavigationWrapper>
          )}
        </FileDetailContainerContentWrapper>
      </FileDetailContainerMainWrapper>
    </FileDetailContainerWrapper>
  );
}

const FileDetailContainerWrapper = styled(GrStack)`
  flex-direction: column;
`;

const FileDetailContainerHeaderWrapper = styled(GrStack)`
  position: sticky;
  top: ${baseHeight.appHeaderHeight};
  margin-top: ${baseHeight.appHeaderHeight};
  width: 100%;
  height: ${baseHeight.appFileDetailHeaderHeight};
  background-color: ${palette["white"]};
  border-bottom: 1px solid ${palette["grey025"]};
  padding: 12px 24px;
  justify-content: space-between;
  align-items: center;
  z-index: 2;

  ${baseMedia.mobile} {
    padding: 12px 16px;
  }
`;

const TitleWrapper = styled(GrStack)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  & > span {
    transition: max-width 0.2s ease-in-out;
    max-width: 400px;
  }

  ${baseMedia.laptop} {
    & > span {
      max-width: 160px;
    }
  }

  ${baseMedia.tablet} {
    display: none;
  }
`;

const FileDetailContainerMainWrapper = styled(GrStack)`
  width: 100%;
  background-color: ${palette.grey010};

  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`;

const FileDetailContainerContentWrapper = styled(GrStack)`
  position: relative;
  width: 100%;
  flex: 1;
  flex-direction: column;
  overflow-y: auto;
  margin-top: 32px;
  margin-bottom: calc(${baseHeight.appFooterHeight} + 32px);
  min-height: calc(100vh - 1.75rem - 56px - 0px - 12rem);
  background-color: ${palette.grey010};
`;

const StyledDocumentView = styled.div`
  margin: 0 auto;
  @media print {
    display: none !important;
  }
`;

const NavigationWrapper = styled(GrStack)`
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 2;
  pointer-events: none;

  @media (max-width: 768px) {
    width: 100% !important;
  }
`;

const DEFAULT_INFORM_VIEW_STYLE: CSSProperties = {
  height: "100%",
  backgroundColor: palette.grey010,
};
